import { create } from 'zustand';
import type { Tier } from '@debtchisel/shared';
import {
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  getCurrentSession,
  refreshSession,
} from '../lib/cognito';
import { api } from '../lib/api';
import { queryClient } from '../lib/queryClient';

const isAuthBypass = import.meta.env.VITE_AUTH_BYPASS === 'true';

interface AuthState {
  userId: string | null;
  email: string | null;
  tier: Tier;
  isAuthenticated: boolean;
  isLoading: boolean;
  idToken: string | null;

  /** Dev bypass login (preserves existing behavior for DevLoginPage). */
  login: (userId: string, tier: Tier) => void;
  /** Real Cognito sign-in. */
  loginWithCognito: (email: string, password: string) => Promise<void>;
  logout: () => void;
  /** Called once on app mount to restore session from localStorage. */
  restoreSession: () => Promise<void>;
  /** Called by API client on 401 to refresh expired tokens. */
  refreshTokens: () => Promise<string | null>;
  setTier: (tier: Tier) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  userId: null,
  email: null,
  tier: 'FREE',
  isAuthenticated: false,
  isLoading: true,
  idToken: null,

  login: (userId, tier) => {
    if (isAuthBypass) {
      localStorage.setItem('dev-session', JSON.stringify({ userId, tier }));
    }
    set({ userId, email: null, tier, isAuthenticated: true, isLoading: false, idToken: null });
  },

  loginWithCognito: async (email, password) => {
    const result = await cognitoSignIn(email, password);
    // Set token first so api client can use it for the billing call
    set({
      userId: result.userId,
      email: result.email,
      tier: 'FREE',
      isAuthenticated: true,
      isLoading: false,
      idToken: result.tokens.idToken,
    });
    try {
      const status = await api.get<{ tier: Tier }>('/v1/billing/status');
      set({ tier: status.tier });
    } catch {
      // Non-blocking — default to FREE if billing check fails
    }
  },

  logout: () => {
    if (isAuthBypass) {
      localStorage.removeItem('dev-session');
    } else {
      cognitoSignOut();
    }
    queryClient.clear();
    set({
      userId: null,
      email: null,
      tier: 'FREE',
      isAuthenticated: false,
      isLoading: false,
      idToken: null,
    });
  },

  restoreSession: async () => {
    if (isAuthBypass) {
      try {
        const saved = localStorage.getItem('dev-session');
        if (saved) {
          const { userId, tier } = JSON.parse(saved) as { userId: string; tier: Tier };
          set({ userId, tier, isAuthenticated: true, isLoading: false });
          return;
        }
      } catch { /* ignore malformed data */ }
      set({ isLoading: false });
      return;
    }

    try {
      const result = await getCurrentSession();
      if (result) {
        set({
          userId: result.userId,
          email: result.email,
          isAuthenticated: true,
          isLoading: false,
          idToken: result.tokens.idToken,
        });
        try {
          const status = await api.get<{ tier: Tier }>('/v1/billing/status');
          set({ tier: status.tier });
        } catch {
          // Non-blocking — default to FREE if billing check fails
        }
      } else {
        set({ isLoading: false });
      }
    } catch {
      set({ isLoading: false });
    }
  },

  refreshTokens: async () => {
    try {
      const result = await refreshSession();
      if (result) {
        set({ idToken: result.tokens.idToken });
        return result.tokens.idToken;
      }
      get().logout();
      return null;
    } catch {
      get().logout();
      return null;
    }
  },

  setTier: (tier) => set({ tier }),
}));
